// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  blockingHolderNames,
  describeBlockingHolders,
  type BlockingGrants,
} from '@/components/integrations/blockingGrants';

export interface BlockingGrantsNoticeProps {
  subject: 'integration' | 'credential';
  blocking: BlockingGrants;
  grantsHref: string;
  integrationName: string | undefined;
  'data-testid'?: string;
}

/** Explains that a delete is refused until every app and user grant is revoked. */
export function BlockingGrantsNotice({
  subject,
  blocking,
  grantsHref,
  integrationName,
  'data-testid': testId,
}: BlockingGrantsNoticeProps): React.JSX.Element {
  const names = blockingHolderNames(blocking);
  const holders = describeBlockingHolders(blocking);
  const one = names.length === 1;

  return (
    <div
      className={cn(
        'rounded-gl border border-gl-amber/30 bg-gl-amber/10 px-3 py-2.5',
        'text-[12.5px] leading-relaxed text-gl-amber-text',
      )}
      role="status"
      data-testid={testId}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="font-medium">
            {holders
              ? `This ${subject} is still granted to ${holders}.`
              : `This ${subject} still has active grants.`}
          </p>
          <p className="break-words">
            {names.length > 0 ? (
              <>
                Unlink {one ? 'it' : 'them'} before deleting by revoking{' '}
                {one ? 'its grant' : 'their grants'}:{' '}
                <span className="font-medium">{names.join(', ')}</span>.
              </>
            ) : (
              'Unlink them before deleting by revoking each grant.'
            )}
          </p>
          <Link to={grantsHref} className="inline-block break-words font-medium underline">
            Review grants for {integrationName ?? 'this integration'}
          </Link>
        </div>
      </div>
    </div>
  );
}
